export const ENVIRONMENTS = ['prod', 'uat', 'stage', 'dev'] as const
export type Environment = (typeof ENVIRONMENTS)[number]

/** Operations YAML `cluster` value (dg_prod) to environment name. */
export function environmentFromOperationsCluster(
  cluster: unknown,
): Environment | undefined {
  if (typeof cluster !== 'string') {
    return undefined
  }
  const env = cluster.replace(/^dg[_-]/, '')
  return (ENVIRONMENTS as readonly string[]).includes(env)
    ? (env as Environment)
    : undefined
}

/** Kubernetes / Grafana / Prometheus cluster label for an environment. */
export function clusterForEnvironment(env: string): string {
  return `dg-k8s-${env}`
}

export function sortEnvironments(envs: Iterable<string>): string[] {
  const order = (e: string) => {
    const i = (ENVIRONMENTS as readonly string[]).indexOf(e)
    return i === -1 ? ENVIRONMENTS.length : i
  }
  return [...new Set(envs)].sort(
    (x, y) => order(x) - order(y) || (x < y ? -1 : 1),
  )
}

export function parseEnvironmentsAnnotation(value?: string): string[] {
  return sortEnvironments(
    (value ?? '')
      .split(',')
      .map(e => e.trim())
      .filter(Boolean),
  )
}

export const TOPOLOGY_SERVICE_DASHBOARD = {
  uid: 'topology-service',
  slug: 'topology-service',
}
export const TOPOLOGY_OVERVIEW_DASHBOARD = {
  uid: 'topology-overview',
  slug: 'topology-service-map',
}

export const TOPOLOGY_PANELS = {
  requestRate: 1,
  errorRate: 2,
  latencyP95: 3,
  callers: 4,
  callees: 5,
  requestRateByCaller: 6,
  cpu: 8,
  memory: 9,
  throttling: 10,
  restarts: 11,
  network: 12,
  replicas: 14,
} as const

function trimBase(baseUrl: string) {
  return baseUrl.replace(/\/+$/, '')
}

export function grafanaServiceDashboardUrl(
  baseUrl: string,
  env: string,
  service: string,
): string {
  const q = new URLSearchParams({
    orgId: '1',
    'var-cluster': clusterForEnvironment(env),
    'var-service': service,
  })
  const { uid, slug } = TOPOLOGY_SERVICE_DASHBOARD
  return `${trimBase(baseUrl)}/d/${uid}/${slug}?${q}`
}

export function grafanaServicePanelUrl(opts: {
  baseUrl: string
  env: string
  service: string
  panelId: number
  theme: 'light' | 'dark'
  from?: string
  to?: string
}): string {
  const q = new URLSearchParams({
    orgId: '1',
    'var-cluster': clusterForEnvironment(opts.env),
    'var-service': opts.service,
    panelId: String(opts.panelId),
    theme: opts.theme,
    from: opts.from ?? 'now-6h',
    to: opts.to ?? 'now',
  })
  const { uid, slug } = TOPOLOGY_SERVICE_DASHBOARD
  return `${trimBase(opts.baseUrl)}/d-solo/${uid}/${slug}?${q}`
}

export function grafanaOverviewUrl(baseUrl: string): string {
  const { uid, slug } = TOPOLOGY_OVERVIEW_DASHBOARD
  return `${trimBase(baseUrl)}/d/${uid}/${slug}?orgId=1`
}
